// Registers the PWA service worker only in production-like environments.
// On each page load we explicitly check for an updated SW and, when one
// takes control, reload the page so clients always run the latest code.
import { logger } from './lib/logger';

// Bump this whenever the SW or core caching strategy changes. If a client's
// stored marker doesn't match, we proactively unregister all SWs and wipe
// caches before re-registering — this rescues users stuck on a buggy older
// SW (e.g. one that intercepted fetches and locked response body streams).
//
// Version history:
//   v4 — Phase 16: removed buggy fetch handler that locked body streams.
//   v5 — Phase 18: post-deploy of blog + music player + DOMPurify hardening.
//        Forces re-purge for users whose browsers held onto a stale SW that
//        was returning "Body is disturbed or locked" on auth callbacks.
const SW_CLIENT_VERSION = 'apex-mind-v5';
const SW_VERSION_KEY = '__apex_sw_client_version';

async function purgeAllServiceWorkersAndCaches() {
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
  } catch (err) {
    // Old browsers / sandboxed contexts may not expose getRegistrations.
    logger.warn('[SW] getRegistrations failed:', err?.message || err);
  }
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
    }
  } catch (err) {
    logger.warn('[SW] cache purge failed:', err?.message || err);
  }
}

export function register() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  // Only register over HTTPS or on localhost
  const isHttps = window.location.protocol === 'https:';
  const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (!isHttps && !isLocalhost) return;

  window.addEventListener('load', async () => {
    // One-time purge for clients that previously had a buggy SW. We compare
    // a localStorage marker against the current client version; if they
    // mismatch (or the marker is missing), we wipe every SW + cache, set the
    // marker, then reload so the page boots fresh against the new SW.
    let needsPurge = false;
    try {
      needsPurge = window.localStorage.getItem(SW_VERSION_KEY) !== SW_CLIENT_VERSION;
    } catch (err) {
      // localStorage unavailable (private browsing, quota). Treat as "needs purge"
      // so the client at least re-fetches the latest SW on every load.
      
      logger.warn('[SW] localStorage read failed; forcing purge:', err?.message || err);
      needsPurge = true;
    }
    if (needsPurge) {
      await purgeAllServiceWorkersAndCaches();
      try {
        window.localStorage.setItem(SW_VERSION_KEY, SW_CLIENT_VERSION);
      } catch (err) {
        // Non-fatal: without the marker we'll just re-purge next load.
        
        logger.warn('[SW] localStorage write failed:', err?.message || err);
      }
      // Reload once so the page is no longer under the control of the stale SW.
      // The SW_VERSION_KEY guard prevents an infinite loop on the next load.
      window.location.reload();
      return;
    }

    navigator.serviceWorker
      .register('/service-worker.js', { scope: '/' })
      .then((registration) => {
        // Force an update check on every full page load. The browser will
        // diff /service-worker.js byte-by-byte and install a new version
        // if one is available.
        registration.update().catch(() => {});
      })
      .catch((err) => {
        if (process.env.NODE_ENV !== 'production') {
          
          logger.warn('SW registration failed:', err);
        }
      });

    // When a NEW service worker takes control of the page (e.g. after the
    // user previously had a buggy version cached on their device), reload
    // once so the fresh code is what's executing. The local `reloaded`
    // flag prevents an infinite reload loop.
    let reloaded = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });
  });
}

export function unregister() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((r) => r.unregister()));
}
